import React, { useState, useEffect } from 'react';
import { List, Typography, Badge, Card, Space, message, Flex, Breadcrumb, Button, Pagination } from 'antd';
import { 
  BellOutlined, 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  UserOutlined, 
  AppstoreAddOutlined, 
  CheckSquareOutlined 
} from '@ant-design/icons';
import { getParamsApi } from "../services/api";
import { Client } from '@stomp/stompjs';

const { Text, Title } = Typography;

const NotificationsPage = ({ updateNotificationCount }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const userEmail = localStorage.getItem("userEmail")?.toLowerCase();
      if (!userEmail) return;

      const response = await getParamsApi("/notification", { owner: userEmail });
      setNotifications(response);

      const unreadCount = response.filter(n => !n.read).length;
      localStorage.setItem('notificationCount', unreadCount);
      updateNotificationCount(unreadCount);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      message.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();

    const userEmail = localStorage.getItem("userEmail")?.toLowerCase();
    if (!userEmail) return;

    const stompClient = new Client({
      brokerURL: 'ws://ec2-54-221-60-147.compute-1.amazonaws.com:8080/ws',
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000
    });

    stompClient.onConnect = () => {
      console.log('Connected to WebSocket');

      stompClient.subscribe(`/user/${userEmail}/notifications`, () => {
        fetchNotifications();
      });
    };

    stompClient.onWebSocketError = (error) => {
      console.error('Error with websocket', error);
      message.error('Failed to connect to notification service');
    };

    stompClient.onStompError = (frame) => {
      console.error('Broker reported error:', frame);
      message.error('Connection error with notification service');
    };

    stompClient.activate();

    return () => {
      if (stompClient.active) {
        stompClient.deactivate();
      }
    };
  }, [updateNotificationCount]);

  const handleNotificationClick = async (id) => {
    try {
      await getParamsApi('/notification/markAsRead', { id });

      await fetchNotifications();
      
      message.success('Marked as read');
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An error occurred while marking notification as read";
      message.error(errorMessage);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const userEmail = localStorage.getItem("userEmail");
      if (!userEmail) return;

      await getParamsApi('/notification/markAll', { owner: userEmail });

      await fetchNotifications();
      
      message.success('All notifications marked as read');
    } catch (error) {
      message.error('Failed to mark all notifications as read');
    }
  };

  const getNotificationIcon = (type) => {
    const iconStyle = { fontSize: '24px' };
    switch (type) {
      case 'badge':
        return <BellOutlined style={iconStyle} />;
      case 'user':
        return <UserOutlined style={iconStyle} />;
      case 'category':
        return <AppstoreAddOutlined style={iconStyle} />;
      default:
        return <BellOutlined style={iconStyle} />;
    }
  };

  const getPaginatedData = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return notifications.slice(startIndex, endIndex);
  };

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  return (
    <div>
      <Flex
        gap="middle"
        align="center"
        justify="space-between"
        className="page-header"
      >
        <h2>Notifications</h2>
        <Breadcrumb
          items={[
            {
              title: "Home",
            },
            {
              title: "Notifications",
            },
          ]}
        />
      </Flex>
      
      <div style={{ padding: '24px' }}>
        <Flex justify="flex-end" style={{ marginBottom: '16px' }}>
          <Button 
            type="primary"
            onClick={handleMarkAllAsRead}
            disabled={!notifications.some(n => !n.read)}
            icon={<CheckSquareOutlined style={{ fontSize: '18px' }} />}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              borderRadius: '6px',
              padding: '4px 16px',
              height: '36px',
              fontSize: '14px',
              boxShadow: '0 2px 0 rgba(0,0,0,0.045)'
            }}
          >
            Mark All as Read
          </Button>
        </Flex>
        <List
          itemLayout="horizontal"
          dataSource={getPaginatedData()}
          renderItem={(item) => (
            <List.Item onClick={() => handleNotificationClick(item.id)}>
              <Card 
                style={{ 
                  width: '100%',
                  backgroundColor: item.read ? '#fff' : '#f0f5ff',
                  transition: 'all 0.3s',
                  cursor: 'pointer'
                }}
                hoverable
              >
                <List.Item.Meta
                  avatar={
                    <Badge dot={!item.read}>
                      {getNotificationIcon(item.type)}
                    </Badge>
                  }
                  title={
                    <Space>
                      <Text strong>{item.title}</Text>
                      {!item.read && (
                        <Badge 
                          count="New" 
                          style={{ 
                            backgroundColor: '#52c41a',
                            fontSize: '12px'
                          }} 
                        />
                      )}
                    </Space>
                  }
                  description={
                    <Space direction="vertical">
                      <Text>{item.content}</Text>
                      <Space>
                        <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
                        <Text type="secondary">
                          {new Date(item.createdDate).toLocaleString()}
                        </Text>
                        {item.read && (
                          <Text type="secondary">
                            <CheckCircleOutlined /> Read
                          </Text>
                        )}
                      </Space>
                    </Space>
                  }
                />
              </Card>
            </List.Item>
          )}
        />
        <Flex justify="end" style={{ marginTop: '20px' }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={notifications.length}
            onChange={handlePageChange}
            showSizeChanger
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
          />
        </Flex>
      </div>
    </div>
  );
};

export default NotificationsPage; 